import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";

const STORAGE_KEY = "tanakh_referral_code";

/**
 * Global referral-code capture.
 *
 * Mounted once at the App root inside <BrowserRouter>. On every route
 * change it inspects `?ref=CODE` and:
 *   1. Persists the code to localStorage so a later /auth/register call
 *      can attribute the signup to the referrer (the AuthContext picks
 *      it up automatically).
 *   2. Fires the public click-tracking endpoint so the admin dashboard
 *      sees the click in the live feed even if the visitor never signs
 *      up.
 *   3. Strips `ref` from the visible URL so the user sees a clean link
 *      and a refresh doesn't double-track the click.
 *
 * Without this, a referral link `https://torahya.com/?ref=ABC123`
 * landed on the public Landing page where no one was watching for the
 * param — only /auth handled it. So if the visitor browsed the site
 * before signing up, the attribution was lost.
 */
export default function ReferralCapture() {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (!ref) return;
    const code = ref.toUpperCase().trim();
    if (!code) return;

    // 1) Persist for downstream attribution at register-time.
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      /* storage may be blocked — ignore, we still try to track */
    }

    // 2) Track click (fire-and-forget; public endpoint, no auth needed).
    api.trackReferralClick(code).catch(() => {});

    // 3) Strip `ref` from the URL so we don't re-track on refresh and
    //    the visible link stays clean.
    params.delete("ref");
    const cleaned =
      location.pathname +
      (params.toString() ? `?${params.toString()}` : "") +
      (location.hash || "");
    window.history.replaceState({}, "", cleaned);
  }, [location.pathname, location.search, location.hash]);

  return null;
}
